/**
 * WidgetDataConfig - Configuration simplifiée des sources de données
 * 
 * Composant unifié pour configurer les données de tous les types de widgets.
 * Affiche les champs appropriés selon le type de configuration (bucket, category, global, etc.)
 */

import React, { useMemo, useCallback } from "react";
import { Typography, Select, Option, Input, Switch } from "@/components/ui";
import { FieldSelectorSimple } from "@/components/field-selector";
import { FaDatabase, FaPlus, FaTrash } from "react-icons/fa";
import { useFields } from "@/api";
import type { AvailableFilter } from "@/models/filter";
import type {
  DataConfig,
  BucketDataConfig,
  CategoryDataConfig,
  GlobalMetricDataConfig,
  DocumentsDataConfig,
  MetricConfig,
  MetricType,
  DateInterval,
  WidgetType,
} from "@/models/widget-v2";
import { WIDGET_TYPES } from "@/models/widget-v2";

// Note: useFields() retourne déjà des AvailableFilter[] avec types normalisés
// Les types normalisés sont: "date", "text", "float", "number", "boolean"

// ============================================================================
// Types
// ============================================================================

interface WidgetDataConfigProps {
  widgetType: WidgetType;
  config: DataConfig;
  onChange: (config: DataConfig) => void;
}

// ============================================================================
// Constants
// ============================================================================

const METRIC_TYPES: { value: MetricType; label: string }[] = [
  { value: "count", label: "Nombre de documents" },
  { value: "sum", label: "Somme" },
  { value: "avg", label: "Moyenne" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
  { value: "cardinality", label: "Valeurs uniques" },
];

const DATE_INTERVALS: { value: DateInterval; label: string }[] = [
  { value: "hour", label: "Par heure" },
  { value: "day", label: "Par jour" },
  { value: "week", label: "Par semaine" },
  { value: "month", label: "Par mois" },
  { value: "year", label: "Par année" },
];


// ============================================================================
// Sub-components
// ============================================================================

interface MetricEditorProps {
  metric: MetricConfig;
  onChange: (metric: MetricConfig) => void;
  onRemove?: () => void;
  showRemove?: boolean;
  numberFields: AvailableFilter[];
}

const MetricEditor: React.FC<MetricEditorProps> = ({
  metric,
  onChange,
  onRemove,
  showRemove = false,
  numberFields,
}) => {
  const needsField = metric.type !== "count";
  
  return (
    <div className="flex gap-2 items-start p-3 bg-arc-surface-secondary rounded-lg">
      <div className="flex-1 space-y-2">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              label="Nom"
              value={metric.name}
              onChange={(e) => onChange({ ...metric, name: e.target.value })}
              placeholder="Nom de la métrique"
              size="sm"
            />
          </div>
          <div className="w-40">
            <Select
              label="Type"
              value={metric.type}
              onChange={(value) => onChange({ 
                ...metric, 
                type: value as MetricType,
                field: value === "count" ? null : metric.field,
              })}
              size="sm"
            >
              {METRIC_TYPES.map(t => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </div>
        </div>
        
        {needsField && (
          <FieldSelectorSimple
            availableFields={numberFields}
            value={metric.field || ""}
            onChange={(field) => onChange({ ...metric, field })}
            placeholder="Sélectionner un champ numérique"
            filterByTypes={["float", "number"]}
            width="100%"
          />
        )}
      </div>
      
      {showRemove && onRemove && (
        <button
          onClick={onRemove}
          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
          title="Supprimer"
        >
          <FaTrash size={14} />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Config Editors by Type
// ============================================================================

interface BucketConfigEditorProps {
  config: BucketDataConfig;
  onChange: (config: BucketDataConfig) => void;
  numberFields: AvailableFilter[];
  textFields: AvailableFilter[];
  allFields: AvailableFilter[];
}

/**
 * Détermine automatiquement le type de bucket en fonction du type du champ
 */
const inferBucketTypeFromFieldType = (fieldType: string): BucketDataConfig["bucketType"] => {
  switch (fieldType) {
    case "date":
      return "date_histogram";
    case "number":
    case "float":
    case "integer":
    case "long":
    case "double":
      return "histogram";
    case "text":
    case "keyword":
    default:
      return "terms";
  }
};

const BucketConfigEditor: React.FC<BucketConfigEditorProps> = ({
  config,
  onChange,
  numberFields,
  textFields,
  allFields,
}) => {
  const handleAddMetric = useCallback(() => {
    onChange({
      ...config,
      metrics: [...config.metrics, { name: `metric_${config.metrics.length + 1}`, type: "count", field: null }],
    });
  }, [config, onChange]);
  
  const handleUpdateMetric = useCallback((index: number, metric: MetricConfig) => {
    const newMetrics = [...config.metrics];
    newMetrics[index] = metric;
    onChange({ ...config, metrics: newMetrics });
  }, [config, onChange]);
  
  const handleRemoveMetric = useCallback((index: number) => {
    if (config.metrics.length <= 1) return;
    onChange({
      ...config,
      metrics: config.metrics.filter((_, i) => i !== index),
    });
  }, [config, onChange]);
  
  // Handler pour le changement de champ - détecte automatiquement le type de bucket
  const handleFieldChange = useCallback((field: string) => {
    // Trouver le type du champ sélectionné
    const selectedField = allFields.find(f => f.key === field);
    const fieldType = selectedField?.type || "text";
    
    // Inférer le type de bucket approprié
    const inferredBucketType = inferBucketTypeFromFieldType(fieldType);
    
    // Mettre à jour la config avec le champ et le type de bucket inféré
    const newConfig: BucketDataConfig = {
      ...config,
      bucketField: field,
      bucketType: inferredBucketType,
    };
    
    // Ajouter les options par défaut selon le type
    if (inferredBucketType === "date_histogram" && !config.dateInterval) {
      newConfig.dateInterval = "month";
    } else if (inferredBucketType === "histogram" && !config.numericInterval) {
      newConfig.numericInterval = 100;
    } else if (inferredBucketType === "terms" && !config.size) {
      newConfig.size = 10;
    }
    
    onChange(newConfig);
  }, [config, onChange, allFields]);
  
  return (
    <div className="space-y-4">
      {/* Champ de groupement */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Champ de groupement
        </Typography>
        <FieldSelectorSimple
          availableFields={allFields}
          value={config.bucketField}
          onChange={handleFieldChange}
          placeholder="Sélectionner un champ"
          width="100%"
        />
      </div>
      
      {/* Options spécifiques au type (détecté automatiquement) */}
      {config.bucketType === "date_histogram" && (
        <div>
          <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
            Intervalle
          </Typography>
          <Select
            value={config.dateInterval || "month"}
            onChange={(value) => onChange({ ...config, dateInterval: value as DateInterval })}
          >
            {DATE_INTERVALS.map(i => (
              <Option key={i.value} value={i.value}>{i.label}</Option>
            ))}
          </Select>
        </div>
      )}
      
      {config.bucketType === "terms" && (
        <div>
          <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
            Nombre de catégories
          </Typography>
          <Input
            type="number"
            value={config.size || 10}
            onChange={(e) => onChange({ ...config, size: parseInt(e.target.value) || 10 })}
            min={1}
            max={100}
          />
        </div>
      )}
      
      {config.bucketType === "histogram" && (
        <div>
          <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
            Intervalle numérique
          </Typography>
          <Input
            type="number"
            value={config.numericInterval || 100}
            onChange={(e) => onChange({ ...config, numericInterval: parseInt(e.target.value) || 100 })}
            min={1}
          />
        </div>
      )}
      
      {/* Split By - Séparer en plusieurs séries */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Séparer par (optionnel)
        </Typography>
        <FieldSelectorSimple
          availableFields={textFields}
          value={config.splitBy || ""}
          onChange={(field) => onChange({ 
            ...config, 
            splitBy: field || undefined,
            splitBySize: field ? (config.splitBySize ?? 20) : undefined,
          })}
          placeholder="Ex: tracker, source, pays..."
          filterByTypes={["text"]}
          width="100%"
        />
        <Typography variant="text-xs" className="text-content-tertiary mt-1">
          Crée une ligne/barre distincte pour chaque valeur du champ
        </Typography>
        {config.splitBy && (
          <div className="mt-2">
            <Input
              type="number"
              label="Nombre max de séries"
              value={config.splitBySize || 20}
              onChange={(e) => onChange({ ...config, splitBySize: parseInt(e.target.value) || 20 })}
              min={1}
              max={50}
            />
          </div>
        )}
      </div>
      
      {/* Métriques */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Typography variant="text-sm" className="text-content-secondary font-medium">
            Métriques
          </Typography>
          <button
            onClick={handleAddMetric}
            className="flex items-center gap-1 text-xs text-accent hover:text-accent-dark transition-colors"
          >
            <FaPlus size={10} />
            Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {config.metrics.map((metric, index) => (
            <MetricEditor
              key={index}
              metric={metric}
              onChange={(m) => handleUpdateMetric(index, m)}
              onRemove={() => handleRemoveMetric(index)}
              showRemove={config.metrics.length > 1}
              numberFields={numberFields}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface CategoryConfigEditorProps {
  config: CategoryDataConfig;
  onChange: (config: CategoryDataConfig) => void;
  textFields: AvailableFilter[];
  numberFields: AvailableFilter[];
}

const CategoryConfigEditor: React.FC<CategoryConfigEditorProps> = ({
  config,
  onChange,
  textFields,
  numberFields,
}) => {
  return (
    <div className="space-y-4">
      {/* Champ de catégorie */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Champ de catégorie
        </Typography>
        <FieldSelectorSimple
          availableFields={textFields}
          value={config.field}
          onChange={(field) => onChange({ ...config, field })}
          placeholder="Sélectionner un champ"
          width="100%"
        />
      </div>
      
      {/* Métrique */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Métrique
        </Typography>
        <MetricEditor
          metric={config.metric}
          onChange={(metric) => onChange({ ...config, metric })}
          numberFields={numberFields}
        />
      </div>
      
      {/* Nombre de catégories */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Nombre de catégories
        </Typography>
        <Input
          type="number"
          value={config.size || 10}
          onChange={(e) => onChange({ ...config, size: parseInt(e.target.value) || 10 })}
          min={1}
          max={50}
        />
      </div>
      
      {/* Options avancées */}
      <div className="flex items-center gap-4">
        <Switch
          label="Afficher les valeurs manquantes"
          checked={config.showMissing || false}
          onChange={(checked) => onChange({ ...config, showMissing: checked })}
        />
      </div>
    </div>
  );
};

interface GlobalConfigEditorProps {
  config: GlobalMetricDataConfig;
  onChange: (config: GlobalMetricDataConfig) => void;
  numberFields: AvailableFilter[];
  dateFields: AvailableFilter[];
  showComparison?: boolean;
}

const GlobalConfigEditor: React.FC<GlobalConfigEditorProps> = ({
  config,
  onChange,
  numberFields,
  dateFields,
  showComparison = true,
}) => {
  const hasComparison = !!config.comparison;
  
  return (
    <div className="space-y-4">
      {/* Métrique principale */}
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Métrique principale
        </Typography>
        <MetricEditor
          metric={config.metric}
          onChange={(metric) => onChange({ ...config, metric })}
          numberFields={numberFields}
        />
      </div>
      
      {/* Comparaison temporelle */}
      {showComparison && (
        <div className="space-y-3">
          <Switch
            label="Activer la comparaison temporelle"
            checked={hasComparison}
            onChange={(checked) => {
              if (checked) {
                onChange({
                  ...config,
                  comparison: {
                    period: "month",
                    dateField: dateFields[0]?.key || "date$date",
                  },
                });
              } else {
                const { comparison: _, ...rest } = config;
                onChange(rest as GlobalMetricDataConfig);
              }
            }}
          />
          
          {hasComparison && config.comparison && (() => {
            const comparison = config.comparison;
            return (
              <div className="pl-4 border-l-2 border-arc-border space-y-3">
                <div>
                  <Typography variant="text-sm" className="text-content-secondary mb-2">
                    Période de comparaison
                  </Typography>
                  <Select
                    value={comparison.period}
                    onChange={(value) => onChange({
                      ...config,
                      comparison: { ...comparison, period: value as NonNullable<GlobalMetricDataConfig["comparison"]>["period"] },
                    })}
                  >
                    <Option value="day">Jour précédent</Option>
                    <Option value="week">Semaine précédente</Option>
                    <Option value="month">Mois précédent</Option>
                    <Option value="quarter">Trimestre précédent</Option>
                    <Option value="year">Année précédente</Option>
                  </Select>
                </div>
                
                <div>
                  <Typography variant="text-sm" className="text-content-secondary mb-2">
                    Champ de date
                  </Typography>
                  <FieldSelectorSimple
                    availableFields={dateFields}
                    value={comparison.dateField}
                    onChange={(field) => onChange({
                      ...config,
                      comparison: { ...comparison, dateField: field },
                    })}
                    placeholder="Sélectionner un champ de date"
                    width="100%"
                  />
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

interface DocumentsConfigEditorProps {
  config: DocumentsDataConfig;
  onChange: (config: DocumentsDataConfig) => void;
}

const DocumentsConfigEditor: React.FC<DocumentsConfigEditorProps> = ({
  config,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <Typography variant="text-sm" className="text-content-secondary mb-2 font-medium">
          Nombre de documents
        </Typography>
        <Input
          type="number"
          value={config.size || 20}
          onChange={(e) => onChange({ ...config, size: parseInt(e.target.value) || 20 })}
          min={1}
          max={100}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const WidgetDataConfig: React.FC<WidgetDataConfigProps> = ({
  widgetType,
  config,
  onChange,
}) => {
  // Charger les champs disponibles
  const { data: fields, isLoading } = useFields();
  
  // Filtrer les champs par type (types normalisés: date, text, float, number, boolean)
  const { dateFields, textFields, numberFields, allFields } = useMemo(() => {
    if (!fields) return { dateFields: [], textFields: [], numberFields: [], allFields: [] };
    
    return {
      dateFields: fields.filter(f => f.type === "date"),
      textFields: fields.filter(f => f.type === "text"),
      numberFields: fields.filter(f => f.type === "float" || f.type === "number"),
      allFields: fields, // Tous les champs pour la sélection du bucket
    };
  }, [fields]);
  
  // Afficher le loading
  if (isLoading) {
    return (
      <div className="p-4 text-center text-content-tertiary">
        Chargement des champs...
      </div>
    );
  }
  
  // Afficher le bon éditeur selon le type de config
  const renderEditor = () => {
    switch (config.kind) {
      case "bucket":
        return (
          <BucketConfigEditor
            config={config}
            onChange={(c) => onChange(c)}
            numberFields={numberFields}
            textFields={textFields}
            allFields={allFields}
          />
        );
        
      case "category":
        return (
          <CategoryConfigEditor
            config={config}
            onChange={(c) => onChange(c)}
            textFields={textFields}
            numberFields={numberFields}
          />
        );
        
      case "global":
        return (
          <GlobalConfigEditor
            config={config}
            onChange={(c) => onChange(c)}
            numberFields={numberFields}
            dateFields={dateFields}
            showComparison={widgetType === WIDGET_TYPES.KPI}
          />
        );
        
      case "documents":
        return (
          <DocumentsConfigEditor
            config={config}
            onChange={(c) => onChange(c)}
          />
        );
        
      case "static":
        return (
          <div className="p-4 text-center text-content-tertiary">
            Ce widget n'a pas besoin de configuration de données.
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-content-secondary">
        <FaDatabase size={16} />
        <Typography variant="text-sm" className="font-semibold">
          Configuration des données
        </Typography>
      </div>
      
      {renderEditor()}
    </div>
  );
};

export default WidgetDataConfig;
